import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import useSWR, { mutate } from "swr"
import useSWRMutation from "swr/mutation"

export const invalidateSavedContactsCache = () =>
    mutate((key) => Array.isArray(key) && key[0] === "saved-contacts")

const getSavedContacts = (orgId: string) =>
    fetchApiGateway("get /org/:orgId/saved-contacts", { params: { orgId } })

export const useSavedContacts = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(orgId ? ["saved-contacts", orgId] : null, () =>
        getSavedContacts(orgId!)
    )

    return { contacts: data?.contacts ?? [], isLoading }
}

type CreateSavedContactArg = { name: string; email?: string; phone?: string }

export const useCreateSavedContact = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-saved-contact",
        (_key, { arg }: { arg: CreateSavedContactArg }) =>
            orgId
                ? fetchApiGateway("put /org/:orgId/saved-contacts", {
                      params: { orgId },
                      body: arg,
                  })
                : undefined,
        { onSuccess: invalidateSavedContactsCache }
    )

    return { createSavedContact: trigger, isCreating: isMutating }
}

type UpdateSavedContactArg = { contactId: string; name?: string; email?: string; phone?: string }

export const useUpdateSavedContact = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-saved-contact",
        (_key, { arg }: { arg: UpdateSavedContactArg }) =>
            orgId
                ? fetchApiGateway("patch /org/:orgId/saved-contacts", {
                      params: { orgId },
                      body: arg,
                  })
                : undefined,
        { onSuccess: invalidateSavedContactsCache }
    )

    return { updateSavedContact: trigger, isUpdating: isMutating }
}

export const useDeleteSavedContact = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-saved-contact",
        (_key, { arg }: { arg: string }) =>
            orgId
                ? fetchApiGateway("delete /org/:orgId/saved-contacts", {
                      params: { orgId },
                      body: { contactId: arg },
                  })
                : undefined,
        { onSuccess: invalidateSavedContactsCache }
    )

    return { deleteSavedContact: trigger, isDeleting: isMutating }
}
